import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { getExcelChangeBatchEligibility } from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { confirmDialog, eventAlert, eventEmitter } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  option: {
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1)
  },
  optionMeta: {
    fontSize: '12px',
    color: theme.palette.text.secondary
  },
  ineligible: {
    fontSize: '12px',
    color: theme.palette.error.main
  },
  differentLevel: {
    fontSize: '12px',
    color: theme.palette.warning.dark,
    fontWeight: 500
  }
}))

// Excel "change timings" moves the student to another eligible Excel batch of
// the same course (program-native). Eligibility returns candidate batches;
// only is_eligible ones are selectable. Candidates span every level, so a
// cross-level pick is flagged in the list and confirmed before it is applied.
const ChangeTimingsForm = ({
  isModalOpen,
  formData,
  onSaveClick,
  closeModal
}) => {
  const classes = useStyles()
  const [batches, setBatches] = useState([])
  const [selectedBatch, setSelectedBatch] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const hasFetchedRef = useRef(false)

  const handleSave = async () => {
    if (!selectedBatch) {
      setError('Please select a batch to move to')
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }

    // Moving across levels is allowed but never silent — the admin has to
    // acknowledge it before the change is applied.
    const batch = batches.find((b) => b.batch === selectedBatch)
    if (batch?.is_different_level) {
      const from = batch.current_level_name || 'no level set'
      const to = batch.level_name || 'no level set'
      const { isConfirmed } = await confirmDialog({
        text: `This batch is at a different level. The student will move from ${from} to ${to}. Continue?`,
        confirmButtonText: 'Yes, change level',
        options: { title: 'Confirm level change' }
      })
      if (!isConfirmed) {
        // ModalForm keeps its Confirm button disabled until it sees this.
        eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
        return
      }
    }

    onSaveClick({ new_batch: selectedBatch }, 'change_timings')
  }

  useEffect(() => {
    if (!isModalOpen || !formData?.uuid || hasFetchedRef.current) return
    hasFetchedRef.current = true

    const checkEligibility = async () => {
      try {
        setIsLoading(true)
        const response = await getExcelChangeBatchEligibility(formData.uuid)
        const candidates = response?.data?.batches || []
        if (!candidates.length) {
          eventAlert(
            'No alternative batches are available for this enrolment.',
            constants.ERROR
          )
          closeModal()
          return
        }
        setBatches(candidates)
      } catch (err) {
        eventAlert(
          'Unable to check batch change eligibility. Please try again.',
          constants.ERROR
        )
        closeModal()
      } finally {
        setIsLoading(false)
      }
    }

    checkEligibility()
  }, [isModalOpen, formData?.uuid, closeModal])

  useEffect(() => {
    if (!isModalOpen) hasFetchedRef.current = false
  }, [isModalOpen])

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      onSaveClick={handleSave}
      modalTitle="Change timings"
      buttonLabel="Confirm"
      maxWidth="md"
      loading={isLoading ? true : undefined}
    >
      <FormControl
        className={classes.formControl}
        component="fieldset"
        error={!!error}
      >
        <RadioGroup
          value={selectedBatch}
          onChange={(e) => {
            setSelectedBatch(e.target.value)
            setError('')
          }}
        >
          {batches.map((b) => (
            <FormControlLabel
              key={b.batch}
              value={b.batch}
              disabled={!b.is_eligible}
              className={classes.option}
              control={<Radio color="primary" />}
              label={
                <Box>
                  <Typography>{b.timings}</Typography>
                  <Typography className={classes.optionMeta}>
                    {b.teacher_name ? `${b.teacher_name} · ` : ''}
                    {`${b.vacancy} seat(s) open`}
                    {b.cost || b.cost === 0 ? ` · ${b.cost} coins/mo` : ''}
                    {b.level_name ? ` · ${b.level_name}` : ''}
                  </Typography>
                  {b.is_different_level && (
                    <Typography className={classes.differentLevel}>
                      {`Different level — currently ${
                        b.current_level_name || 'no level set'
                      }`}
                    </Typography>
                  )}
                  {!b.is_eligible && b.batch_change_ineligibility_reason && (
                    <Typography className={classes.ineligible}>
                      {b.batch_change_ineligibility_reason}
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    </ModalForm>
  )
}

ChangeTimingsForm.propTypes = {
  isModalOpen: PropTypes.bool,
  formData: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default ChangeTimingsForm
